import type {
  CourseCertificateBulkIssueResponse,
  CourseClassroomAttendeeAddBatchRequest,
  CourseClassroomAttendeeAddRequest,
  CourseClassroomAttendeeBatchUpdateRequest,
  CourseClassroomAttendeeResponse,
  CourseClassroomAttendeeRosterRowResponse,
  CourseClassroomAttendeeUpdateRequest,
  CourseClassroomCloseoutPreviewResponse,
  CourseClassroomCloseoutReceipt,
  CourseClassroomRequest,
  CourseClassroomResponse,
  CourseClassroomUndoCloseoutReceipt,
  GetCourseClassroomListRequest,
  GetCourseClassroomListRequestV2,
  GetListResponseOfCourseClassroomSimpleResponse,
} from 'src/api/ProactApiClient';
import {courseClassroomApiClient} from 'src/api/ProactApiClientInstances';
import {emptySplitApi} from '../api';
import {clientQuery, voidClientQuery} from '../clientQuery';

// Query args live in the Redux store as cache keys, so Date instances are not
// allowed (non-serializable). Callers pass ISO strings; the generated client
// serializes the body with JSON.stringify, which accepts either form.
export type GetCourseClassroomListV2QueryArgs = Omit<
  GetCourseClassroomListRequestV2,
  'startsAfter' | 'startsBefore'
> & {
  startsAfter?: string;
  startsBefore?: string;
};

export const courseClassroomApi = emptySplitApi.injectEndpoints({
  endpoints: builder => ({
    getCourseClassroomList: builder.query<
      GetListResponseOfCourseClassroomSimpleResponse,
      GetCourseClassroomListRequest
    >({
      queryFn: clientQuery(params => courseClassroomApiClient.list(params)),
      providesTags: ['CourseClassrooms'],
    }),
    getCourseClassroomListV2: builder.query<
      GetListResponseOfCourseClassroomSimpleResponse,
      GetCourseClassroomListV2QueryArgs
    >({
      queryFn: clientQuery(params =>
        courseClassroomApiClient.listV2(
          params as unknown as GetCourseClassroomListRequestV2
        )
      ),
      providesTags: ['CourseClassrooms'],
    }),
    getCourseClassroom: builder.query<CourseClassroomResponse, number>({
      queryFn: clientQuery(id => courseClassroomApiClient.get(id)),
      providesTags: ['CourseClassrooms'],
    }),
    createCourseClassroom: builder.mutation<
      CourseClassroomResponse,
      CourseClassroomRequest
    >({
      queryFn: clientQuery(model => courseClassroomApiClient.create(model)),
      invalidatesTags: ['CourseClassrooms'],
    }),
    updateCourseClassroom: builder.mutation<
      CourseClassroomResponse,
      CourseClassroomRequest
    >({
      queryFn: clientQuery(model => courseClassroomApiClient.update(model)),
      invalidatesTags: ['CourseClassrooms'],
    }),
    getCourseClassroomRoster: builder.query<
      CourseClassroomAttendeeRosterRowResponse[],
      number
    >({
      queryFn: clientQuery<number, CourseClassroomAttendeeRosterRowResponse[]>(
        async id => {
          const rows = await courseClassroomApiClient.getRoster(id);
          return rows.map(row => ({
            ...row,
            certificateSessionId: row.certificateSessionId ?? undefined,
          }));
        }
      ),
      providesTags: ['CourseClassrooms'],
    }),
    addCourseClassroomAttendee: builder.mutation<
      CourseClassroomAttendeeResponse,
      CourseClassroomAttendeeAddRequest
    >({
      queryFn: clientQuery(request => courseClassroomApiClient.addAttendee(request)),
      invalidatesTags: ['CourseClassrooms'],
    }),
    addCourseClassroomAttendeeBatch: builder.mutation<
      CourseClassroomAttendeeResponse[],
      CourseClassroomAttendeeAddBatchRequest
    >({
      queryFn: clientQuery(request =>
        courseClassroomApiClient.addAttendeeBatch(request)
      ),
      invalidatesTags: ['CourseClassrooms'],
    }),
    updateCourseClassroomAttendee: builder.mutation<
      CourseClassroomAttendeeResponse,
      CourseClassroomAttendeeUpdateRequest
    >({
      queryFn: clientQuery(request =>
        courseClassroomApiClient.updateAttendee(request)
      ),
      invalidatesTags: ['CourseClassrooms'],
    }),
    updateCourseClassroomAttendeeBatch: builder.mutation<
      CourseClassroomAttendeeResponse[],
      CourseClassroomAttendeeBatchUpdateRequest
    >({
      queryFn: clientQuery(request =>
        courseClassroomApiClient.updateAttendeeBatch(request)
      ),
      invalidatesTags: ['CourseClassrooms'],
    }),
    removeCourseClassroomAttendee: builder.mutation<void, number>({
      queryFn: voidClientQuery(attendeeId =>
        courseClassroomApiClient.removeAttendee(attendeeId)
      ),
      invalidatesTags: ['CourseClassrooms'],
    }),
    getCourseClassroomCloseoutPreview: builder.query<
      CourseClassroomCloseoutPreviewResponse,
      number
    >({
      queryFn: clientQuery(id => courseClassroomApiClient.getCloseoutPreview(id)),
      providesTags: ['CourseClassrooms'],
    }),
    closeOutCourseClassroom: builder.mutation<
      CourseClassroomCloseoutReceipt,
      number
    >({
      queryFn: clientQuery(id => courseClassroomApiClient.closeOut(id)),
      invalidatesTags: ['CourseClassrooms'],
    }),
    undoCourseClassroomCloseout: builder.mutation<
      CourseClassroomUndoCloseoutReceipt,
      number
    >({
      queryFn: clientQuery(id => courseClassroomApiClient.undoCloseout(id)),
      invalidatesTags: ['CourseClassrooms'],
    }),
    issueCourseClassroomCertificates: builder.mutation<
      CourseCertificateBulkIssueResponse,
      number
    >({
      queryFn: clientQuery(id => courseClassroomApiClient.issueCertificates(id)),
      invalidatesTags: ['CourseClassrooms'],
    }),
  }),
});

export const {
  useGetCourseClassroomListQuery,
  useGetCourseClassroomListV2Query,
  useGetCourseClassroomQuery,
  useCreateCourseClassroomMutation,
  useUpdateCourseClassroomMutation,
  useGetCourseClassroomRosterQuery,
  useAddCourseClassroomAttendeeMutation,
  useAddCourseClassroomAttendeeBatchMutation,
  useUpdateCourseClassroomAttendeeMutation,
  useUpdateCourseClassroomAttendeeBatchMutation,
  useRemoveCourseClassroomAttendeeMutation,
  useGetCourseClassroomCloseoutPreviewQuery,
  useCloseOutCourseClassroomMutation,
  useUndoCourseClassroomCloseoutMutation,
  useIssueCourseClassroomCertificatesMutation,
} = courseClassroomApi;
